import {
  ChangeEvent,
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MdKeyboardArrowDown as KeyboardArrowDownOutlinedIcon } from 'react-icons/md';
import { MdKeyboardArrowUp as KeyboardArrowUpOutlinedIcon } from 'react-icons/md';
import { MdPeople as PeopleIcon } from 'react-icons/md';
import { makeStyles } from 'tss-react/mui';
import {
  ClickAwayListener,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  Popper,
  Theme,
  Typography,
} from '@mui/material';
import { MdSearch as SearchIcon } from 'react-icons/md';
import AppOwnerList from './AppOwnerList';
import { MdGroups as GroupsIcon } from 'react-icons/md';
import { setDisplaySelectedAppOwners } from '../../../application-owners/api/applications-owners-api';

const AppOwnerSelector: FC<React.PropsWithChildren<AppOwnerSelectorProps>> = ({
  appOwnersFilterOptions,
  onChange,
}) => {
  const { classes } = useStyles();
  const [options, setOptions] = useState<
    {
      email: string;
      name: string;
      checked: boolean;
    }[]
  >(appOwnersFilterOptions);
  const [filter, setFilter] = useState<string>('');
  const [entireOrg, setEntireOrg] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setOptions(appOwnersFilterOptions);
  }, [appOwnersFilterOptions]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      setAnchorEl(open ? null : event.currentTarget);
    },
    [open],
  );

  const onClickAway = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const filterOwners = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value.toLowerCase().trimStart());
  }, []);

  useEffect(() => {
    if (appOwnersFilterOptions.some(o => o.checked)) {
      setEntireOrg(false);
    }
  }, [appOwnersFilterOptions]);

  const onOptionSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      const selected = event.target.value;
      if (selected === AppOwnerDefault.EntireOrganization) {
        checked &&
          setOptions(options.map(option => ({ ...option, checked: false })));
        setEntireOrg(checked);
        checked && setDisplaySelectedAppOwners([]);
        checked && onChange && onChange();
        return;
      }
      const updatedOptions = options.map(option =>
        option.email === selected ? { ...option, checked } : { ...option },
      );
      setEntireOrg(updatedOptions.some(o => o.checked) ? false : true);
      setOptions(updatedOptions);
      setDisplaySelectedAppOwners(
        updatedOptions.filter(o => o.checked).map(owner => owner.email),
      );
      onChange && onChange();
    },
    [onChange, options],
  );

  const selectedOwners = useMemo(() => {
    const owners = options.filter(option => option.checked);
    return owners.length > 0
      ? owners.length > 1
        ? ` ${owners[0].name} (+${owners.length - 1})`
        : owners[0].name
      : AppOwnerDefault.EntireOrganization;
  }, [options]);

  const id = open ? 'show-issues-data-pop-over' : undefined;
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className={classes.showIssuesFor}>
        <div
          onClick={handleClick}
          aria-describedby={id}
          className={classes.selectedOwners}>
          {entireOrg ? (
            <GroupsIcon fontSize='small' />
          ) : (
            <PeopleIcon fontSize='small' />
          )}
          <Typography
            textAlign='center'
            lineHeight={2}
            className={classes.textOverflow}
            variant='body2'>
            {selectedOwners}
          </Typography>
          {open ? (
            <IconButton color='inherit' size='small'>
              <KeyboardArrowUpOutlinedIcon />
            </IconButton>
          ) : (
            <IconButton color='inherit' size='small'>
              <KeyboardArrowDownOutlinedIcon />
            </IconButton>
          )}
        </div>
        <Popper
          placement='bottom'
          className={classes.popper}
          id={id}
          open={open}
          anchorEl={anchorEl}>
          <div className={classes.search}>
            <FormControl color='secondary' fullWidth variant='outlined'>
              <Input
                value={filter}
                onChange={filterOwners}
                placeholder='Search'
                startAdornment={
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                }
              />
            </FormControl>
          </div>
          <AppOwnerList
            filter={filter}
            options={options}
            onOptionSelect={onOptionSelect}
            entireOrg={entireOrg}
          />
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export enum AppOwnerDefault {
  EntireOrganization = 'Entire Organization',
}

const useStyles = makeStyles()((theme: Theme) => ({
  search: {
    padding: theme.spacing(1),
  },
  textOverflow: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    flexGrow: 1,
  },
  popper: {
    zIndex: theme.zIndex.drawer + 2,
    borderRadius: theme.shape.borderRadius,
    width: '12.5rem',
    boxShadow: theme.shadows['6'],
    backgroundColor: theme.palette.background.paper,
  },
  selectedOwners: {
    cursor: 'pointer',
    height: '2.1rem',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    paddingInline: theme.spacing(1),
    border: `1px solid ${theme.palette.text.secondary}`,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
  },
  showIssuesFor: {
    width: '12.5rem',
  },
}));
export interface AppOwnerSelectorProps {
  onChange?: () => void;
  appOwnersFilterOptions: {
    email: string;
    name: string;
    checked: boolean;
  }[];
}

export default AppOwnerSelector;
